import React, { memo, useState } from 'react';
import { motion } from 'framer-motion';
import { AudioWaveform } from 'lucide-react';
import { WallpapersResponse } from '@/lib/collections/wallpapers';

function isLiveWallpaper(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  if (stored === 'live-wallpaper') return true;
  if (stored && stored !== 'live-wallpaper') return false;
  const url = (wallpaper.imageUrl || '').toLowerCase().split('?')[0];
  return /\.(mp4|webm|mov|m4v)$/.test(url);
}

function isRingtone(wallpaper: WallpapersResponse): boolean {
  const stored = (wallpaper as WallpapersResponse & { mediaType?: string }).mediaType;
  return stored === 'ringtone';
}

function getCardImageUrl(wallpaper: WallpapersResponse): string {
  const baseUrl = wallpaper.imageUrl;
  if (!baseUrl) return '';
  const stripped = baseUrl.split('?')[0];
  const isUnsplash = stripped.includes('unsplash.com') || stripped.includes('images.unsplash');
  if (!isUnsplash) return baseUrl;
  return `${stripped}?w=600&q=85&auto=format&fit=crop`;
}

interface ProfileGridCardProps {
  wallpaper: WallpapersResponse;
  onClick: () => void;
  index?: number;
}

export const ProfileGridCard: React.FC<ProfileGridCardProps> = memo(function ProfileGridCard({
  wallpaper,
  onClick,
  index = 0,
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const live = isLiveWallpaper(wallpaper);
  const ringtone = isRingtone(wallpaper);
  const imageUrl = getCardImageUrl(wallpaper);

  return (
    <>
      <motion.div
        onClick={onClick}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.22, delay: index * 0.03, ease: 'easeOut' }}
        whileTap={{ scale: 0.95 }}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          position: 'relative',
          aspectRatio: '1 / 1',
          borderRadius: 10,
          overflow: 'hidden',
          cursor: 'pointer',
          background: '#0f0f1a',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
          touchAction: 'manipulation',
        } as React.CSSProperties}
      >
      {/* ── Ringtone card: dark bg + centered waveform icon ─────────────────── */}
      {ringtone && (
        <>
          {/* Layered dark background */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(145deg, #0d0d1a 0%, #111124 50%, #0a0a16 100%)',
          }} />
          {/* Subtle radial glow */}
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(167,139,250,0.1) 0%, transparent 70%)',
          }} />
          {/* Centered waveform icon */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AudioWaveform
              size={36}
              color="rgba(167,139,250,0.65)"
              strokeWidth={1.5}
            />
          </div>
        </>
      )}

      {/* ── Image / video card ───────────────────────────────────────────────── */}
      {!ringtone && imageUrl && (
        <>
          {/* Shimmer placeholder while loading */}
          {!imageLoaded && (
            <div style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(90deg, #0f0f1a 25%, #181828 50%, #0f0f1a 75%)',
              backgroundSize: '200% 100%',
              animation: 'pgcShimmer 1.4s infinite',
            }} />
          )}

          {live ? (
            <video
              src={imageUrl}
              autoPlay
              loop
              muted
              playsInline
              onCanPlay={() => setImageLoaded(true)}
              onContextMenu={(e) => e.preventDefault()}
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
              disableRemotePlayback
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
          ) : (
            <div
              style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
              ref={(el) => {
                if (el && !imageLoaded) {
                  const img = new Image();
                  img.onload = () => setImageLoaded(true);
                  img.src = imageUrl;
                }
              }}
            />
          )}
        </>
      )}

      <style>{`
        @keyframes pgcShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </motion.div>
    </>
  );
});

ProfileGridCard.displayName = 'ProfileGridCard';
